/**
 * Upload ETA math — pure functions behind the Step-1 aggregate progress strip
 * (round 4). No I/O, no DOM, no store imports; the screen samples the store's
 * existing per-photo progress on a ~1s interval and feeds it through these.
 *
 * Model: cumulative `doneBytes` samples over a rolling time window give a
 * throughput average (bytes/sec); ETA = remaining bytes ÷ rate. The first few
 * seconds have no trustworthy rate → callers show "estimating…" (rollingRate
 * returns null until the window spans ETA_MIN_SPAN_MS).
 */

export interface EtaSample {
  /** Sample timestamp (ms epoch or any monotonic ms clock). */
  t: number;
  /** Cumulative uploaded bytes at `t` (monotonic while a batch runs). */
  bytes: number;
}

/** Rolling window the throughput average is computed over. */
export const ETA_WINDOW_MS = 15_000;
/** Minimum span of data before quoting a rate (before this: "estimating…"). */
export const ETA_MIN_SPAN_MS = 3_000;

/** Photo statuses that count as an in-flight upload (drives strip visibility). */
export function isUploadInFlight(status: string): boolean {
  return status === 'queued' || status === 'decoding' || status === 'hashing' || status === 'uploading';
}

/** Append a sample, pruning everything older than the window. Pure — returns a new array. */
export function pushSample(
  samples: readonly EtaSample[],
  t: number,
  bytes: number,
  windowMs: number = ETA_WINDOW_MS,
): EtaSample[] {
  const cutoff = t - windowMs;
  return [...samples.filter((s) => s.t >= cutoff), { t, bytes }];
}

/**
 * Rolling throughput in bytes/second: (last − first) over the window. Returns
 * null when there's not enough data yet (fewer than 2 samples, a span shorter
 * than `minSpanMs`) or no forward progress (stall / files removed) — callers
 * render "estimating…" for null.
 */
export function rollingRate(
  samples: readonly EtaSample[],
  minSpanMs: number = ETA_MIN_SPAN_MS,
): number | null {
  if (samples.length < 2) return null;
  const first = samples[0]!;
  const last = samples[samples.length - 1]!;
  const dtMs = last.t - first.t;
  if (dtMs < minSpanMs) return null;
  const dBytes = last.bytes - first.bytes;
  if (dBytes <= 0) return null;
  return dBytes / (dtMs / 1000);
}

/** Seconds left for `remainingBytes` at `rate` bytes/sec; null = unknown (show "estimating…"). */
export function etaSeconds(remainingBytes: number, rate: number | null): number | null {
  if (rate == null || rate <= 0) return null;
  if (remainingBytes <= 0) return 0;
  return remainingBytes / rate;
}

/** `45` → `~45s left`, `130` → `~2m 10s left`, `3900` → `~1h 5m left`. */
export function formatEta(seconds: number): string {
  const s = Math.max(1, Math.round(seconds));
  if (s < 60) return `~${s}s left`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return rem > 0 ? `~${m}m ${rem}s left` : `~${m}m left`;
  const h = Math.floor(m / 60);
  const mRem = m % 60;
  return mRem > 0 ? `~${h}h ${mRem}m left` : `~${h}h left`;
}

export interface BatchProgress {
  /** All bytes this batch will move (completed-this-batch + in-flight files). */
  totalBytes: number;
  /** Bytes already moved (completed-this-batch + per-file partial progress). */
  doneBytes: number;
  /** Whole-number 0..100 (100 only when everything landed). */
  percent: number;
}

/**
 * Aggregate a photo list into batch byte totals. `baselineReadyBytes` is the
 * Σbytes of photos that were ALREADY 'ready' when the batch started — they are
 * excluded so a resumed project's old uploads don't skew the strip. Files that
 * error out drop from both sides automatically.
 */
export function batchProgress(
  photos: ReadonlyArray<{ status: string; bytes: number; progress: number }>,
  baselineReadyBytes: number,
): BatchProgress {
  let readyBytes = 0;
  let inFlightTotal = 0;
  let inFlightDone = 0;
  for (const p of photos) {
    if (p.status === 'ready') readyBytes += p.bytes;
    else if (isUploadInFlight(p.status)) {
      inFlightTotal += p.bytes;
      inFlightDone += (p.bytes * Math.min(100, Math.max(0, p.progress))) / 100;
    }
  }
  const sessionReady = Math.max(0, readyBytes - baselineReadyBytes);
  const totalBytes = sessionReady + inFlightTotal;
  const doneBytes = Math.min(totalBytes, sessionReady + inFlightDone);
  const percent = totalBytes > 0 ? Math.min(100, Math.floor((doneBytes / totalBytes) * 100)) : 0;
  return { totalBytes, doneBytes, percent };
}
